import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cloneDeep, isEqual } from 'lodash';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button, Tabs, FormChangesUI, DropdownButton } from '@praktio/common-ui';
import { Container, Col, Row } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import classNames from 'classnames';

// Ducks
import { RootState } from '../../../ducks/store';
import { clearTeam, getTeam, getTeams } from '../../../ducks/teams';
import { getUsers } from '../../../ducks/users';
import { getCourses } from '../../../ducks/courses';

// Components
import User from '../../admin/User';
import FormField from '../../common/FormField';
import { BreadcrumbItem, Breadcrumbs } from '../../common/Breadcrumbs/Breadcrumbs';
import SendWelcomeEmailModal from '../SendWelcomeEmailModal';
import TeamSettingsTab from './TeamSettingsTab';
import TeamCoursesTab from './TeamCoursesTab';
import TeamLearnersTab from './TeamLearnersTab';
import TeamManagersTab from './TeamManagersTab';
import RightPane from '../../common/RightPane/RightPane';
import BulkReportNotifications from '../BulkReportNotifications/BulkReportNotifications';
import TeamName from '../../common/TeamName/TeamName';
import UserCourseProgressDetails from '../UserCourseProgressDetails/UserCourseProgressDetails';
import OverlaySpinner from '../../common/OverlaySpinner/OverlaySpinner';

// Constants/Utils
import { Roles } from '../../../utils/constants';
import { updateSearchParams, useQuery } from '../../../utils/functions';

import './Team.scss';
import ManagerLearnerAssignments from '../ManagerLearnerAssignments/ManagerLearnerAssignments';
import ResetProgressModal from '../modals/ResetProgressModal/ResetProgressModal';

export interface IFullCourseListData {
  assignedToTeam: ICourse[];
  notAssignedToTeam: ICourse[];
}

const TeamTabs = {
  Settings: 'settings',
  Courses: 'courses',
  Learners: 'learners',
  Managers: 'managers',
  Legacy: 'legacy',
};

const TeamTasks = {
  BulkSetReportNotifications: 'bulkSetReportNotifications',
  LearnerAssignments: 'learnerAssignments',
};

const Team = () => {
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const teamId = params.teamId as string;
  const activeTab = searchParams.get('tab') || 'settings';
  const selectedUserId = searchParams.get('userId') || undefined;
  const task = searchParams.get('task') || undefined;
  const courseId = searchParams.get('courseId');
  const query = useQuery();
  const parentId = query.get('parentId');
  const isNew = teamId === 'new';
  const dispatch = useDispatch();
  const { data, teamsById } = useSelector((state: RootState) => {
    const { teams } = state;
    const { current } = teams;

    const teamsById: { [key: string]: ITeam } = {};
    state.teams.list?.forEach((team) => (teamsById[team._id] = team));
    return {
      data: current,
      teamsById,
    };
  });

  const [team, setTeam] = useState(data || ({ parent: parentId } as ITeam));
  const prevTeam = useRef<ITeam | undefined>();
  const prevTab = useRef<string>(activeTab);
  const prevParentId = useRef<string | null | undefined>(parentId);
  const [showOverlay, setShowOverlay] = useState(false);
  const [showResetAllProgressModal, setShowResetAllProgressModal] = useState(false);
  const [showSendWelcomeEmailModal, setShowSendWelcomeEmailModal] = useState(false);

  const [adjustingCourseAssignments, setAdjustingCourseAssignments] = useState(false);
  const [editingLearnerAssignments, setEditingLearnerAssignments] = useState(false);
  const [editingManagerAssignments, setEditingManagerAssignments] = useState(false);

  const [rightPaneOpen, setRightPaneOpen] = useState(
    !!selectedUserId || task === TeamTasks.BulkSetReportNotifications,
  );

  // TODO - Create multiple "hasChanges" and refs/clones so that comparisons
  // can be made on more specific levels.
  // done: settingsHasChanges, coursesHasChanges,
  // still need: learnersHasChanges, managersHasChanges.

  // useEffect for teamId changes. If this changes,
  // we need to get the existing team from the service
  // or clear out for a new team setup
  useEffect(() => {
    dispatch(getTeams());
    dispatch(getCourses());
    dispatch(getUsers());
    if (!isNew) {
      dispatch(getTeam(teamId));
    } else {
      dispatch(clearTeam());
    }
    setSavedChanges(false);
  }, [teamId, isNew, parentId, dispatch]);

  // useEffect for redux store data changes. If this changes,
  // set the local team to the incoming data
  useEffect(() => {
    if (!isEqual(prevTeam.current, data) || prevParentId.current !== parentId) {
      setTeam(data || ({ parent: parentId } as ITeam));
    }

    prevTeam.current = cloneDeep(data);
    prevParentId.current = parentId;
  }, [data, parentId, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearTeam());
      setSavedChanges(false);
    };
  }, [dispatch]);

  useEffect(() => {
    if (prevTab.current !== activeTab) {
      if ([TeamTabs.Settings, TeamTabs.Courses, TeamTabs.Legacy].includes(activeTab)) {
        setRightPaneOpen(false);
      }
    } else if (rightPaneOpen) {
      // TODO: Is there a better way to do these conditions instead of
      // having separate ifs?
      if (
        activeTab === TeamTabs.Managers &&
        !selectedUserId &&
        task !== TeamTasks.BulkSetReportNotifications
      ) {
        setRightPaneOpen(false);
      } else if (activeTab === TeamTabs.Learners && !selectedUserId) {
        setRightPaneOpen(false);
      }
    }
    prevTab.current = activeTab;
  }, [activeTab, rightPaneOpen, selectedUserId, task]);

  // Team Settings data
  const settingsRef = useRef<IForwardProps>(null);
  const [settingsHasChanges, setSettingsHasChanges] = useState(false);

  // Team Courses data
  const coursesRef = useRef<IForwardProps>(null);
  const [coursesHasChanges, setCoursesHasChanges] = useState(false);
  const [editingCourseAssignments, setEditingCourseAssignments] = useState(false);

  // Team Learners
  const learnersRef = useRef<IForwardProps>(null);
  const [learnersTabHasChanges, setLearnersTabHasChanges] = useState(false);

  // Team Managers
  const managersRef = useRef<IForwardProps>(null);
  const [managersTabHasChanges, setManagersTabHasChanges] = useState(false);

  const [savedChanges, setSavedChanges] = useState(false);

  const teamManagers = ((team?.users || []) as IUser[]).filter((u) => {
    return u.roles?.find(
      (r) => (r.teamId === teamId || r.subteamId === teamId) && r.role === Roles.TeamManager,
    );
  });

  const orderedAncestors = [team];
  let id: string | undefined | null = team?.parent || parentId;
  while (id) {
    const parent: ITeam = teamsById[id];
    if (parent) {
      orderedAncestors.unshift(parent);
      id = parent.parent;
    } else {
      break;
    }
  }

  const teamTabs = [
    {
      title: 'Settings',
      key: TeamTabs.Settings,
      content: (
        <TeamSettingsTab
          notifyHasChanges={setSettingsHasChanges}
          setSavedChanges={setSavedChanges}
          ref={settingsRef}
        />
      ),
    },
  ];

  if (!isNew) {
    teamTabs.push(
      {
        title: 'Courses',
        key: TeamTabs.Courses,
        content: (
          <TeamCoursesTab
            notifyHasChanges={setCoursesHasChanges}
            setSavedChanges={setSavedChanges}
            editing={editingCourseAssignments}
            ref={coursesRef}
          />
        ),
      },
      {
        title: 'Learners',
        key: TeamTabs.Learners,
        content: (
          <TeamLearnersTab
            adjustingCourseAssignments={adjustingCourseAssignments}
            editingLearnerAssignments={editingLearnerAssignments}
            ref={learnersRef}
            notifyHasChanges={setLearnersTabHasChanges}
            setAdjustingCourseAssignments={setAdjustingCourseAssignments}
            setEditingLearnerAssignments={setEditingLearnerAssignments}
            setRightPaneOpen={setRightPaneOpen}
            setSavedChanges={setSavedChanges}
          />
        ),
      },
      {
        title: 'Managers',
        key: TeamTabs.Managers,
        content: (
          <TeamManagersTab
            editingManagerAssignments={editingManagerAssignments}
            ref={managersRef}
            teamId={teamId}
            teamManagers={teamManagers}
            notifyHasChanges={setManagersTabHasChanges}
            setEditingManagerAssignments={setEditingManagerAssignments}
            setRightPaneOpen={setRightPaneOpen}
            setSavedChanges={setSavedChanges}
          />
        ),
      },
    );
  }

  if (team?.legacyData) {
    // TODO: This tab holds legacy data, will remove when we're off litmos/done testing
    teamTabs.push({
      title: 'Legacy Data',
      key: TeamTabs.Legacy,
      content: (
        <>
          <FormField labelName="Team Id">{team?.legacyData.Id}</FormField>
          <div>Course Assignments</div>
          {team?.legacyData?.CourseAssignments?.map((ca: any) => (
            <div id={ca.courseId}>
              CourseId: {ca.courseId} Assigned:
              {ca.assigned ? new Date(ca.assigned).toLocaleString() : undefined}
            </div>
          ))}
        </>
      ),
    });
  }

  const renderContent = (activeTab: string) => {
    switch (activeTab) {
      case TeamTabs.Settings:
        return (
          <FormChangesUI unsavedChanges={settingsHasChanges} savedChanges={savedChanges}>
            <Button
              disabled={!settingsHasChanges}
              onClick={() => (settingsRef.current?.save ? settingsRef.current?.save() : null)}
              btnStyle="primary"
            >
              SAVE
            </Button>
          </FormChangesUI>
        );
      case TeamTabs.Courses:
        if (editingCourseAssignments) {
          return (
            <FormChangesUI unsavedChanges={coursesHasChanges} savedChanges={savedChanges}>
              <Button
                onClick={() => {
                  setEditingCourseAssignments(false);
                }}
                className="secondary"
              >
                CANCEL
              </Button>
              <Button
                disabled={!coursesHasChanges}
                onClick={() => {
                  if (coursesRef.current?.save) {
                    coursesRef.current?.save();
                    setEditingCourseAssignments(false);
                  }
                }}
                btnStyle="primary"
              >
                SAVE
              </Button>
            </FormChangesUI>
          );
        } else {
          return (
            <Button
              onClick={() => {
                setSavedChanges(false);
                setEditingCourseAssignments(true);
              }}
            >
              Edit Course Assignments
            </Button>
          );
        }
      case TeamTabs.Learners:
        if (adjustingCourseAssignments || editingLearnerAssignments) {
          return (
            <FormChangesUI
              unsavedChanges={learnersTabHasChanges}
              // TODO - incorporate the actual savedChanges state when the API
              // updates have gone into effect
              savedChanges={savedChanges}
            >
              <Button
                onClick={() => {
                  if (learnersRef.current?.cancel) {
                    learnersRef.current?.cancel();
                  }
                }}
                className="secondary"
              >
                CANCEL
              </Button>
              <Button
                disabled={!learnersTabHasChanges}
                onClick={() => {
                  if (learnersRef.current?.save) {
                    learnersRef.current.save();
                  }
                }}
                btnStyle="primary"
              >
                SAVE
              </Button>
            </FormChangesUI>
          );
        }
        return (
          <DropdownButton
            mainButton={{
              text: 'Add New User',
              action: () => {
                setRightPaneOpen(true);
                updateSearchParams({
                  fields: [
                    {
                      name: 'userId',
                      newValue: 'new',
                    },
                  ],
                  searchParams,
                  setSearchParams,
                });
              },
            }}
            subButtons={[
              {
                text: 'Edit Learner Assignments',
                action: () => {
                  setEditingLearnerAssignments(true);
                },
              },
              {
                text: 'Adjust Course Assignments',
                action: () => {
                  setAdjustingCourseAssignments(true);
                },
              },
              {
                text: 'Send Welcome Email',
                action: () => setShowSendWelcomeEmailModal(true),
              },
              {
                text: `Reset All Progress`,
                action: () => setShowResetAllProgressModal(true),
              },
            ]}
          />
        );
      case TeamTabs.Managers:
        if (editingManagerAssignments) {
          return (
            <FormChangesUI
              unsavedChanges={managersTabHasChanges}
              // TODO - incorporate the actual savedChanges state when the API
              // updates have gone into effect
              savedChanges={savedChanges}
            >
              <Button
                onClick={() => {
                  if (managersRef.current?.cancel) {
                    managersRef.current?.cancel();
                  }
                }}
                className="secondary"
              >
                CANCEL
              </Button>
              <Button
                disabled={!managersTabHasChanges}
                onClick={() => {
                  if (managersRef.current?.save) {
                    managersRef.current.save();
                  }
                }}
                btnStyle="primary"
              >
                SAVE
              </Button>
            </FormChangesUI>
          );
        }
        return (
          <DropdownButton
            mainButton={{
              text: 'Add New User',
              action: () => {
                setRightPaneOpen(true);
                updateSearchParams({
                  fields: [
                    {
                      name: 'userId',
                      newValue: 'new',
                    },
                  ],
                  searchParams,
                  setSearchParams,
                });
              },
            }}
            subButtons={[
              {
                text: 'Edit Manager Assignments',
                action: () => {
                  setEditingManagerAssignments(true);
                },
              },
              {
                text: 'Bulk Set Report Notifications',
                action: () => {
                  setRightPaneOpen(true);
                  updateSearchParams({
                    fields: [
                      {
                        name: 'userId',
                        remove: true,
                      },
                      {
                        name: 'task',
                        newValue: TeamTasks.BulkSetReportNotifications,
                      },
                    ],
                    searchParams,
                    setSearchParams,
                  });
                },
              },
            ]}
          />
        );
    }
  };

  const loading = !isNew && !team?._id;
  if (loading) {
    return <OverlaySpinner />;
  }

  let rightPaneContent = null;
  if (!task && !!selectedUserId && [TeamTabs.Learners, TeamTabs.Managers].includes(activeTab)) {
    if (activeTab === TeamTabs.Learners && !!courseId) {
      rightPaneContent = (
        <UserCourseProgressDetails
          closeButtonAction={() => {
            setSearchParams('');
          }}
        />
      );
    } else {
      rightPaneContent = (
        <User
          closeButtonAction={() => {
            setRightPaneOpen(false);
            updateSearchParams({
              fields: [
                {
                  name: 'userId',
                  remove: true,
                },
              ],
              searchParams,
              setSearchParams,
            });
          }}
        />
      );
    }
  } else if (activeTab === TeamTabs.Managers) {
    const closeButtonAction = () => {
      updateSearchParams({
        fields: [
          {
            name: 'task',
            remove: true,
          },
          { name: 'userId', remove: true },
        ],
        searchParams,
        setSearchParams,
      });
      setRightPaneOpen(false);
    };

    if (task === TeamTasks.BulkSetReportNotifications) {
      rightPaneContent = (
        <BulkReportNotifications
          teamId={teamId}
          teamManagers={teamManagers}
          closeButtonAction={closeButtonAction}
        />
      );
    } else if (task === TeamTasks.LearnerAssignments) {
      rightPaneContent = <ManagerLearnerAssignments closeButtonAction={closeButtonAction} />;
    }
  }

  return (
    <Container fluid className="Team">
      <Helmet>
        <title>{team?.name || 'Team'} - Praktio</title>
      </Helmet>
      <Row className="h-100">
        <Col
          className={classNames('px-3 w-100 h-100 Team-wrapper', {
            'd-none d-md-flex flex-column col-md-5 col-lg-7': rightPaneOpen,
            'd-flex flex-column col-xs-12 px-md-4 px-lg-5': !rightPaneOpen,
          })}
        >
          <div className="Team-header w-100 d-flex justify-content-between sticky-top">
            <div className="left-content">
              <Breadcrumbs>
                {!!orderedAncestors &&
                  orderedAncestors.map((ancestorItem, index) => {
                    return (
                      <BreadcrumbItem
                        key={`ancestor-breadcrumb-${ancestorItem._id}`}
                        target={`/admin/teams/${ancestorItem._id}`}
                        active={index === orderedAncestors.length - 1}
                      >
                        <TeamName team={ancestorItem} />
                      </BreadcrumbItem>
                    );
                  })}
              </Breadcrumbs>
              <h3 className="TeamName text-left font-weight-bold mb-3">
                <TeamName team={team} />
              </h3>
            </div>
            <div className="right-content d-flex align-items-center">
              {renderContent(activeTab)}
            </div>
          </div>
          <div className="Team-content pb-4">
            <Tabs
              tabs={teamTabs}
              activeKey={activeTab}
              setTabInQuery={(nextInit) => {
                updateSearchParams({
                  fields: [
                    {
                      name: 'tab',
                      newValue: nextInit.tab,
                    },
                  ],
                  searchParams,
                  setSearchParams,
                });
              }}
              id="TeamTabs"
              className="sticky-top"
            />
          </div>
        </Col>
        <RightPane open={rightPaneOpen}>{rightPaneContent}</RightPane>
      </Row>
      <SendWelcomeEmailModal
        onHide={() => setShowSendWelcomeEmailModal(false)}
        show={showSendWelcomeEmailModal}
        teamId={teamId}
      />
      <ResetProgressModal
        show={showResetAllProgressModal}
        onHide={() => setShowResetAllProgressModal(false)}
        teamId={teamId}
        setShowOverlay={setShowOverlay}
        all
      />
      {!!showOverlay && (
        <div className="overlay-wrapper">
          <OverlaySpinner />
        </div>
      )}
    </Container>
  );
};

export default Team;
